import type { RouteLocationNormalized, Router } from "vue-router";

export type ListFilters = Record<string, unknown>;

export interface ListFiltersSnapshot {
  initFilters: ListFilters;
  filters: ListFilters;
  page: number;
  perPage: number | undefined;
  showFilters: boolean;
  isDataLoaded: boolean;
  formsFilters: Record<string, ListFilters>;
  navigationId: number;
}

const MAX_SNAPSHOTS = 50;

const MAX_ENTITY_SNAPSHOTS = 20;

const snapshots = new Map<string, ListFiltersSnapshot>();

const entitySnapshots = new Map<string, ListFiltersSnapshot>();

const claimedKeys = new Set<string>();

let isHistoryPopPending = false;

let isCurrentNavigationHistoryPop = false;

let historyPopTarget: RouteLocationNormalized | null = null;

let navigationId = 0;

export const trackHistoryPopNavigations = (router: Router): void => {
  router.options.history.listen(() => {
    isHistoryPopPending = true;
  });

  router.beforeEach((to) => {
    navigationId += 1;

    const isRedirectOfHistoryPop =
      historyPopTarget !== null && (to.redirectedFrom as unknown) === historyPopTarget;

    isCurrentNavigationHistoryPop = isHistoryPopPending || isRedirectOfHistoryPop;

    if (!isRedirectOfHistoryPop) {
      historyPopTarget = isHistoryPopPending ? to : null;
    }

    isHistoryPopPending = false;
  });

  router.afterEach(() => {
    isHistoryPopPending = false;
  });

  router.onError(() => {
    isHistoryPopPending = false;
  });
};

export const isLastNavigationHistoryPop = (): boolean => isCurrentNavigationHistoryPop;

export const currentNavigationId = (): number => navigationId;

export const readListFiltersSnapshot = (
  key: string,
  isEntityScoped = false,
): ListFiltersSnapshot | null => (isEntityScoped ? entitySnapshots : snapshots).get(key) ?? null;

export const saveListFiltersSnapshot = (
  key: string,
  snapshot: ListFiltersSnapshot,
  isEntityScoped = false,
): void => {
  const scopeSnapshots = isEntityScoped ? entitySnapshots : snapshots;

  scopeSnapshots.delete(key);

  scopeSnapshots.set(key, snapshot);

  if (scopeSnapshots.size > (isEntityScoped ? MAX_ENTITY_SNAPSHOTS : MAX_SNAPSHOTS)) {
    scopeSnapshots.delete(scopeSnapshots.keys().next().value as string);
  }
};

export const clearListFiltersMemory = (): void => {
  snapshots.clear();

  entitySnapshots.clear();
};

export const claimListFiltersKey = (baseKey: string): string => {
  let instanceNumber = 1;

  while (claimedKeys.has(`${baseKey}#${instanceNumber}`)) {
    instanceNumber += 1;
  }

  const key = `${baseKey}#${instanceNumber}`;

  claimedKeys.add(key);

  return key;
};

export const releaseListFiltersKey = (key: string): void => {
  claimedKeys.delete(key);
};

export const cloneListFilters = <Filters extends object = ListFilters>(
  filters: Filters | null | undefined,
): Filters => JSON.parse(JSON.stringify(filters ?? {}));
